import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { PACKAGES, STEPS } from "@/data/content";
import { Eyebrow, Lotus } from "./Ornaments";
import { Reveal, scrollToId } from "./Reveal";

const PackageCard = ({ p, i, onPick }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -8 }}
    data-testid={`package-card-${p.id}`}
    className={`relative rounded-[32px] p-8 sm:p-10 flex flex-col ${p.featured ? "bg-blush border-2 border-pink lg:-mt-6 lg:mb-6" : "bg-white border border-terra/10"}`}
  >
    {p.featured && <span className="absolute -top-4 left-8 rounded-full bg-pink text-ink px-4 py-1.5 text-xs font-semibold tracking-wider uppercase">Most loved</span>}
    <span className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${p.accent}33` }}>
      <Lotus className="w-7 h-5" color={p.accent} />
    </span>
    <h3 className="font-display text-4xl text-ink mt-6">{p.name}</h3>
    <p className="text-sm text-muted-ink mt-1">{p.subtitle}</p>
    <p className="font-display text-2xl text-terra-deep mt-6" data-testid={`package-price-${p.id}`}>{p.price}</p>
    <ul className="mt-6 space-y-3 flex-1">
      {p.features.map((f) => (
        <li key={f} className="flex items-start gap-3 text-sm text-ink/90">
          <span className="mt-0.5 w-5 h-5 rounded-full bg-sage/20 flex items-center justify-center shrink-0"><Check size={12} className="text-sage-deep" /></span>{f}
        </li>
      ))}
    </ul>
    <button data-testid={`package-select-${p.id}`} onClick={() => { onPick(p.name); scrollToId("enquiry"); }} className={`mt-8 justify-center ${p.featured ? "btn-pink" : "btn-ghost"}`}>
      Customise {p.name}
    </button>
  </motion.div>
);

export const Packages = ({ onPick }) => (
  <section id="packages" data-testid="packages-section" className="py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal className="text-center max-w-2xl mx-auto mb-16">
        <Eyebrow className="justify-center">Custom packages</Eyebrow>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink mt-5 leading-[1.05]">Start simple, <span className="italic text-terra">add what you love.</span></h2>
        <p className="text-base md:text-lg text-muted-ink mt-6">Every package is a starting point — we tailor guests, rituals, decor and budget with you.</p>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-6 items-stretch">
        {PACKAGES.map((p, i) => <PackageCard key={p.id} p={p} i={i} onPick={onPick} />)}
      </div>
      <div className="mt-24 grid sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="process-steps">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.08}>
            <p className="font-display italic text-5xl text-gold">{s.n}</p>
            <h4 className="font-display text-2xl text-ink mt-3">{s.title}</h4>
            <p className="text-sm text-muted-ink mt-2 leading-relaxed">{s.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
